import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAction } from 'convex/react';
import { api } from '@catchup/convex/convex/_generated/api';
import type { Id } from '@catchup/convex/convex/_generated/dataModel';
import { LoadingScreen } from '../components/LoadingScreen';
import { ErrorScreen } from '../components/ErrorScreen';

type ErrorCode = 'EXPIRED' | 'INVALID' | 'EVENT_NOT_FOUND' | 'PARTICIPANT_NOT_FOUND';

type GroupInviteParticipant = {
  _id?: Id<'participants'>;
  id?: Id<'participants'>;
  participantId?: Id<'participants'>;
  displayName?: string;
  name?: string;
  role?: string;
  isOrganizer?: boolean;
  avatarUrl?: string;
};

type GroupInviteValidation = {
  event: {
    title?: string;
    organizerName?: string;
    timeframe?: string;
    duration?: string;
    location?: {
      name?: string;
      address?: string;
    };
  };
  participants?: GroupInviteParticipant[];
};

type GroupInviteError = {
  error: string;
  code?: ErrorCode;
};

function isValidationError(
  result: GroupInviteValidation | GroupInviteError
): result is GroupInviteError {
  return 'error' in result;
}

function participantId(participant: GroupInviteParticipant): Id<'participants'> | '' {
  return participant._id ?? participant.participantId ?? participant.id ?? '';
}

function participantName(participant: GroupInviteParticipant) {
  return participant.displayName ?? participant.name ?? 'Guest';
}

function participantInitial(participant: GroupInviteParticipant) {
  const name = participantName(participant);
  return name.trim().charAt(0).toUpperCase() || 'G';
}

function individualInvitePath(result: unknown) {
  if (typeof result === 'string') {
    return result.startsWith('/i/') ? result : `/i/${result}`;
  }

  if (result && typeof result === 'object') {
    const record = result as Record<string, unknown>;
    const path = record.path ?? record.url ?? record.inviteUrl;
    const token = record.token ?? record.inviteToken;

    if (typeof path === 'string') {
      return path.startsWith('/i/') ? path : new URL(path, window.location.origin).pathname;
    }

    if (typeof token === 'string') {
      return `/i/${token}`;
    }
  }

  return null;
}

function validationErrorFromUnknown(error: unknown): GroupInviteError {
  const message = error instanceof Error ? error.message : 'Failed to validate invite link';
  const isExpired = /expired/i.test(message);

  return {
    error: message,
    code: isExpired ? 'EXPIRED' : 'INVALID',
  };
}

export default function GroupInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const validateGroupInvite = useAction(api.groupInviteLinks.validateGroupInvite);
  const claimGroupInviteParticipant = useAction(api.groupInviteLinks.claimGroupInviteParticipant);

  const [validation, setValidation] = useState<GroupInviteValidation | GroupInviteError>();
  const [claimingParticipantId, setClaimingParticipantId] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<string>('');
  const [showHelp, setShowHelp] = useState<boolean>(false);

  useEffect(() => {
    if (!token || validation) {
      return;
    }

    validateGroupInvite({ token })
      .then((result: GroupInviteValidation | GroupInviteError) => {
        setValidation(result);
      })
      .catch((error: unknown) => {
        setValidation(validationErrorFromUnknown(error));
      });
  }, [token, validation, validateGroupInvite]);

  const invitedParticipants = useMemo(() => {
    if (!validation || isValidationError(validation)) {
      return [];
    }

    return (validation.participants ?? []).filter((participant) => {
      return participant.role !== 'organizer' && !participant.isOrganizer;
    });
  }, [validation]);

  if (!token || validation === undefined) {
    return <LoadingScreen />;
  }

  if (isValidationError(validation)) {
    return <ErrorScreen message={validation.error} code={validation.code} />;
  }

  const handleClaim = async (participant: GroupInviteParticipant) => {
    const id = participantId(participant);

    if (!id) {
      setClaimError('Could not identify that invitee. Please ask for a new invite.');
      return;
    }

    setClaimingParticipantId(id);
    setClaimError('');

    try {
      const result = await claimGroupInviteParticipant({
        token,
        participantId: id,
      });
      const path = individualInvitePath(result);

      if (path) {
        navigate(path);
        return;
      }

      setClaimError('Could not open your invite. Please try again.');
    } catch {
      setClaimError('Could not open your invite. Please try again.');
    } finally {
      setClaimingParticipantId(null);
    }
  };

  const title = validation.event.title ?? 'this hangout';
  const organizerName = validation.event.organizerName ?? 'Your friend';
  const timeframe = validation.event.timeframe;
  const locationName = validation.event.location?.name;
  const totalPeople = invitedParticipants.length + 1;

  return (
    <main className="min-h-screen bg-[#FAF8FB] px-5 py-8 font-[-apple-system,BlinkMacSystemFont,'SF_Pro_Text','Inter','Helvetica_Neue',Arial,sans-serif] text-[#15131C] antialiased">
      <div className="mx-auto max-w-md">
        {/* Hero */}
        <div className="px-2 pb-4 pt-2 text-center">
          <div className="mb-2 inline-grid place-items-center text-[#5B4DE0]" aria-hidden="true">
            <svg width="40" height="34" viewBox="0 0 40 34" fill="none">
              <circle cx="13" cy="10" r="5.2" stroke="currentColor" strokeWidth="2" />
              <circle cx="26" cy="10" r="5.2" stroke="currentColor" strokeWidth="2" />
              <path d="M3 30c0-5 4.5-9 10-9s10 4 10 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M17 30c0-5 4.5-9 10-9s10 4 10 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M33 3l.6 1.8L35.5 5.5l-1.9.7L33 8l-.6-1.8L30.5 5.5l1.9-.7L33 3z" fill="currentColor" />
            </svg>
          </div>
          <h1 className="m-0 mb-2 text-[26px] font-bold leading-[1.18] tracking-[-0.01em]">
            Welcome! <span aria-hidden="true">✨</span>
          </h1>
          <p className="mx-auto max-w-[300px] text-sm leading-[1.4] text-[#6B6880]">
            {organizerName} invited you to {title}. Please let us know who you are.
          </p>
        </div>

        {/* Event card */}
        <div className="mb-1 flex items-center gap-3.5 rounded-[20px] border border-[#ECEAF1] bg-white p-4 shadow-[0_6px_18px_rgba(54,48,98,.07)]">
          <div className="grid h-14 w-14 flex-shrink-0 place-items-center rounded-2xl bg-[#EFEBFF] text-[#5B4DE0]" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="2" />
              <path d="M3 9h18" stroke="currentColor" strokeWidth="2" />
              <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 mb-2 text-[17px] font-bold leading-[1.22] text-[#15131C]">{title}</h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[12.5px] text-[#6B6880]">
              {timeframe ? (
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 text-[#5B4DE0]">
                    <rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="2" />
                    <path d="M3 9h18" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  {timeframe}
                </span>
              ) : null}
              {locationName ? (
                <span className="inline-flex items-center gap-1 whitespace-nowrap text-[#2C2840]">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 text-[#5B4DE0]">
                    <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" stroke="currentColor" strokeWidth="2" />
                    <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  {locationName}
                </span>
              ) : null}
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 text-[#5B4DE0]">
                  <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="2" />
                  <circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
                  <path d="M3 19c0-3 3-5 6-5s6 2 6 5M14 17c0-2 2-3.5 4-3.5s4 1.5 4 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                {totalPeople} people <span className="text-[#A6A3B8]">· including you</span>
              </span>
            </div>
          </div>
        </div>

        {/* Section head */}
        <section aria-labelledby="select-name-heading">
          <div className="mx-1 mb-3 mt-6">
            <h2 id="select-name-heading" className="m-0 mb-1 text-[17px] font-bold text-[#15131C]">
              Who are you?
            </h2>
            <p className="m-0 text-[13px] text-[#6B6880]">Select your name to continue.</p>
          </div>

          {claimError ? (
            <p
              role="alert"
              className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
            >
              {claimError}
            </p>
          ) : null}

          {/* Name list */}
          <div className="flex flex-col gap-2.5">
            {invitedParticipants.map((participant) => {
              const id = participantId(participant);
              const isClaiming = claimingParticipantId === id;
              const anyClaiming = claimingParticipantId !== null;
              const name = participantName(participant);

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleClaim(participant)}
                  disabled={anyClaiming}
                  aria-busy={isClaiming}
                  className="flex w-full items-center gap-3.5 rounded-2xl border border-[#ECEAF1] bg-white px-3.5 py-3 text-left shadow-[0_1px_2px_rgba(20,18,40,.04)] transition hover:border-[#D8D3E8] hover:shadow-[0_6px_18px_rgba(54,48,98,.07)] active:scale-[.99] focus:outline-none focus:ring-2 focus:ring-[#5B4DE0] disabled:cursor-wait disabled:opacity-60"
                >
                  <div className="relative h-11 w-11 flex-shrink-0">
                    <div className="grid h-11 w-11 place-items-center overflow-hidden rounded-full bg-[#F1EEF7] text-base font-semibold text-[#5B4DE0]">
                      {participant.avatarUrl ? (
                        <img
                          src={participant.avatarUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span aria-hidden="true">{participantInitial(participant)}</span>
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold leading-[1.2] text-[#15131C]">{name}</div>
                    <div className="mt-0.5 flex items-center gap-1 text-[12.5px] text-[#6B6880]">
                      <span
                        aria-hidden="true"
                        className="inline-block h-1 w-1 rounded-full bg-[#5B4DE0]"
                      />
                      {isClaiming ? 'Opening invite…' : `Invited by ${organizerName}`}
                    </div>
                  </div>
                  <svg
                    aria-hidden="true"
                    className="flex-shrink-0 text-[#A6A3B8]"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M9 6l6 6-6 6"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              );
            })}
          </div>
        </section>

        {/* Privacy callout */}
        <div className="mt-[18px] flex items-center gap-3 rounded-2xl border border-[#E5DFFB] bg-[#F5F2FF] px-3.5 py-3">
          <div className="grid h-[30px] w-[30px] flex-shrink-0 place-items-center rounded-lg bg-white text-[#5B4DE0]" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M9 12l2.2 2.2L15 10.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <b className="mb-0.5 block text-[12.5px] font-semibold leading-[1.3] text-[#2C2840]">
              Your response is private
            </b>
            <span className="text-[11.5px] leading-[1.35] text-[#6B6880]">
              Only your availability will be shared with the group
            </span>
          </div>
          <svg
            aria-hidden="true"
            className="flex-shrink-0 text-[#A6A3B8]"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
          >
            <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M8 11V8a4 4 0 1 1 8 0v3" stroke="currentColor" strokeWidth="2" />
          </svg>
        </div>

        {/* Help link */}
        <button
          type="button"
          onClick={() => setShowHelp((prev) => !prev)}
          className="mt-[22px] flex w-full cursor-pointer items-center justify-center gap-1.5 border-none bg-transparent p-2 text-[13px] font-medium text-[#5B4DE0] hover:text-[#4A3CD0]"
        >
          <span className="grid h-[18px] w-[18px] place-items-center rounded-full border-[1.5px] border-current text-[11px] font-bold leading-none">
            ?
          </span>
          Don&apos;t see your name?
        </button>

        {showHelp ? (
          <p className="mx-auto mt-2 max-w-[300px] text-center text-[12.5px] text-[#6B6880]">
            Reach out to {organizerName} to be added to this invite.
          </p>
        ) : null}
      </div>
    </main>
  );
}
